export const content = {
  "identity": {
    "name": "Salman Ahmed Khan",
    "role": "Software and automation builder",
    "location": "Islamabad, Pakistan",
    "eyebrow": "Software / AI automation / Systems integration",
    "headline": [
      "Useful software.",
      "Connected systems.",
      "Less manual work."
    ],
    "introduction": "I lead IT and automation at LotiaSons, connecting software with business and industrial workflows. My work spans PolyBoard kitchen-design automation, browser tools, and responsive applications.",
    "availability": "Head of IT & Automation at LotiaSons"
  },
  "quickFacts": [
    {
      "label": "Current role",
      "value": "IT & Automation / LotiaSons"
    },
    {
      "label": "Applied work",
      "value": "PolyBoard MCP MVP"
    },
    {
      "label": "Public product",
      "value": "Firefox extension"
    },
    {
      "label": "Based in",
      "value": "Islamabad, PK"
    }
  ],
  "projects": [
    {
      "number": "01",
      "slug": "polyboard",
      "title": "PolyBoard MCP",
      "descriptor": "Kitchen design & estimating / Workplace MVP",
      "period": "At LotiaSons",
      "statement": "From a kitchen enquiry to a design, a material breakdown, and a cost estimate.",
      "summary": "An MCP integration for PolyBoard that automates reusable kitchen templates and material/pricing calculations.",
      "role": "Built the MCP MVP and automated the kitchen-design templates and estimating workflow.",
      "constraint": "Responding to a client means coordinating design work, material requirements, and project pricing.",
      "decision": "Use reusable PolyBoard templates and connect the design and calculation steps through MCP.",
      "result": "Our team can prepare kitchen designs and cost estimates faster when responding to clients.",
      "proof": [
        {
          "value": "MVP",
          "label": "implementation stage"
        },
        {
          "value": "MCP",
          "label": "tool integration"
        },
        {
          "value": "Templates",
          "label": "repeatable design work"
        },
        {
          "value": "Estimates",
          "label": "materials & pricing"
        }
      ],
      "notes": [
        "Reusable kitchen-design templates",
        "Material and pricing calculations",
        "Faster responses to client enquiries"
      ],
      "stack": [
        "PolyBoard",
        "MCP",
        "Workflow automation"
      ],
      "preview": "polyboard",
      "theme": "ink",
      "contactLabel": "Discuss a similar workflow"
    },
    {
      "number": "02",
      "slug": "galaxy",
      "title": "Git Galaxy Finder",
      "descriptor": "Browser extension / Public source",
      "period": "Public releases · 2024–2026",
      "statement": "Find the repository you saved, without digging through a long Stars list.",
      "summary": "A published Firefox add-on that searches GitHub Stars lists by repository name and description, highlights matches, and adapts to light and dark themes.",
      "role": "Product idea, extension UI, React and TypeScript implementation, and public releases.",
      "constraint": "Add useful search to GitHub's existing interface while supporting different themes and repository content.",
      "decision": "Keep the search on the Stars page, with visible matches and an interface that follows the host theme.",
      "result": "A Firefox add-on visitors can install, with public source and downloadable releases.",
      "proof": [
        {
          "value": "Firefox",
          "label": "published add-on"
        },
        {
          "value": "Search",
          "label": "names & descriptions"
        },
        {
          "value": "Themes",
          "label": "light & dark"
        },
        {
          "value": "Public",
          "label": "source & releases"
        }
      ],
      "notes": [
        "Repository name and description search",
        "Highlighted search matches",
        "Light and dark presentation",
        "Published Firefox add-on"
      ],
      "stack": [
        "React",
        "TypeScript",
        "WebExtensions",
        "Primer React",
        "Parcel"
      ],
      "liveLabel": "Install on Firefox",
      "live": "https://addons.mozilla.org/en-US/firefox/addon/git-star/",
      "source": "https://github.com/slmkhanahmed/Git-Galaxy-Finder",
      "preview": "galaxy",
      "theme": "ink",
      "image": "/assets/git-galaxy-preview.webp",
      "imageAlt": "Git Galaxy Finder searching a GitHub Stars list and highlighting matching text",
      "imageWidth": 973,
      "imageHeight": 590
    },
    {
      "number": "03",
      "slug": "feedback",
      "title": "Product Feedback App",
      "descriptor": "Front-end demo / Responsive dashboard",
      "period": "May – June 2024",
      "statement": "A clear, sortable view of dense product feedback.",
      "summary": "A React and TypeScript front-end demo for browsing product requests, filtering categories, and sorting by votes or comments.",
      "role": "Responsive implementation, reusable cards, category filters, and dashboard state.",
      "constraint": "Keep feedback and controls readable across desktop, tablet, and mobile layouts.",
      "decision": "Reuse the card and control patterns while changing their composition for each screen size.",
      "result": "A deployed front-end demo with category filtering, four sorting modes, and responsive layouts.",
      "proof": [
        {
          "value": "4 sorts",
          "label": "votes & comments"
        },
        {
          "value": "Filters",
          "label": "feedback categories"
        },
        {
          "value": "Layouts",
          "label": "desktop to mobile"
        },
        {
          "value": "Demo",
          "label": "front-end scope"
        }
      ],
      "notes": [
        "Category filtering",
        "Four sorting options",
        "Responsive feedback cards",
        "Frontend Mentor challenge implementation"
      ],
      "stack": [
        "React",
        "TypeScript",
        "Tailwind CSS",
        "Vite",
        "Context"
      ],
      "liveLabel": "Open the demo",
      "live": "https://product-feedback-app-lovat.vercel.app/",
      "source": "https://github.com/slmkhanahmed/responsive",
      "preview": "feedback",
      "theme": "orange",
      "image": "/assets/product-feedback-preview.webp",
      "imageAlt": "Product Feedback demo with category filters, sorting, and feedback cards",
      "imageWidth": 1440,
      "imageHeight": 900
    }
  ],
  "capabilities": [
    {
      "number": "01",
      "title": "AI & workflow automation",
      "description": "Connect repetitive work through MCP, local LLMs, and reusable scripts or templates.",
      "tools": "MCP / Ollama / PowerShell / APIs"
    },
    {
      "number": "02",
      "title": "Systems integration",
      "description": "Follow a problem across applications, databases, networks, and the equipment they support.",
      "tools": "SQL Server / Windows / Networking"
    },
    {
      "number": "03",
      "title": "Software & interfaces",
      "description": "Build responsive applications with clear controls, reusable components, and considered states.",
      "tools": "React / TypeScript / Node.js"
    },
    {
      "number": "04",
      "title": "Browser tools",
      "description": "Add focused capabilities to the websites people already use.",
      "tools": "WebExtensions / DOM APIs / Parcel"
    },
    {
      "number": "05",
      "title": "Industrial workflows",
      "description": "Connect design tools, material calculations, and estimating with the wider operation.",
      "tools": "PolyBoard / CAD-CAM / CNC / G-code"
    }
  ],
  "process": [
    {
      "number": "01",
      "title": "Trace",
      "detail": "Understand the real workflow and follow the problem across system boundaries.",
      "artifact": "workflow.map"
    },
    {
      "number": "02",
      "title": "Structure",
      "detail": "Define inputs, outputs, ownership, and the steps worth making repeatable.",
      "artifact": "system.notes"
    },
    {
      "number": "03",
      "title": "Build",
      "detail": "Connect the tools, templates, and interfaces that make the workflow useful.",
      "artifact": "integration"
    },
    {
      "number": "04",
      "title": "Verify",
      "detail": "Check the result against real behavior, include recovery, and document what matters.",
      "artifact": "validation.log"
    }
  ],
  "sideQuest": {
    "label": "Engineering side quest / Upstream fork",
    "title": "Pekka Kana 2: Greta contribution",
    "description": "Focused C++ and SDL2 work on an existing platformer fork: quick-save/load controls, rendering and scaling improvements, and a packaged Windows x64 release. Original game and upstream authors are credited in the repository.",
    "notes": [
      "F5 quick-save / F9 quick-load",
      "Rendering & scaling improvements",
      "Windows x64 release"
    ],
    "source": "https://github.com/slmkhanahmed/pk2_greta"
  },
  "codeLedger": [
    {
      "number": "01",
      "kind": "Firefox add-on",
      "title": "Git Galaxy Finder",
      "signal": "Published product",
      "description": "Search saved GitHub repositories by name or description without leaving the Stars page.",
      "stack": "React / TypeScript / WebExtensions",
      "href": "https://github.com/slmkhanahmed/Git-Galaxy-Finder",
      "action": "Inspect source"
    },
    {
      "number": "02",
      "kind": "Responsive dashboard",
      "title": "Product Feedback",
      "signal": "Front-end demo",
      "description": "Filter and sort product requests across desktop, tablet, and mobile layouts.",
      "stack": "React / TypeScript / Tailwind CSS",
      "href": "https://github.com/slmkhanahmed/responsive",
      "action": "Inspect source"
    },
    {
      "number": "03",
      "kind": "Portfolio",
      "title": "Systems Field Notes",
      "signal": "Built in public",
      "description": "Case studies, interface labs, keyboard navigation, and a responsive portfolio.",
      "stack": "React / CSS / Testing Library",
      "href": "https://github.com/slmkhanahmed/slmkhanahmed.github.io",
      "action": "View repository"
    },
    {
      "number": "04",
      "kind": "Credited upstream fork",
      "title": "Pekka Kana 2: Greta",
      "signal": "Windows release work",
      "description": "C++ and SDL2 contributions and release packaging for an existing platformer fork.",
      "stack": "C++ / SDL2 / Windows",
      "href": "https://github.com/slmkhanahmed/pk2_greta",
      "action": "Inspect contribution"
    }
  ],
  "about": {
    "title": "Curiosity crosses system boundaries.",
    "paragraphs": [
      "I am Salman Ahmed Khan, Head of IT & Automation at LotiaSons. My work moves between software development, IT operations, AI tools, and industrial systems.",
      "That range is practical: one task may involve a SQL Server or network issue; another, connecting PolyBoard to a repeatable kitchen-design and estimating workflow. I also build browser tools and product interfaces in public."
    ],
    "principles": [
      "Trace the whole problem.",
      "Automate the repeatable work.",
      "Make the result clear to the person using it.",
      "Verify, document, and keep refining."
    ]
  },
  "journey": [
    {
      "period": "Current",
      "title": "Head of IT & Automation",
      "place": "LotiaSons",
      "description": "Hands-on work across software, IT infrastructure, AI/MCP automation, databases, and industrial systems integration."
    },
    {
      "period": "2024 – Present",
      "title": "Independent software projects",
      "place": "Public-source work",
      "description": "Browser tooling, responsive interfaces, and contributions to an existing C++/SDL2 game."
    },
    {
      "period": "Education",
      "title": "Intermediate in Computer Science (ICS)",
      "place": "Civil College",
      "description": "Computer-science studies alongside practical software development."
    },
    {
      "period": "2018 – 2020",
      "title": "Computer Science Matric",
      "place": "Ideal Cambridge School",
      "description": "The academic starting point for programming and independent building."
    }
  ],
  "contact": {
    "email": "slmkhanahmed@gmail.com",
    "location": "Islamabad, Pakistan",
    "github": "https://github.com/slmkhanahmed",
    "linkedin": "https://www.linkedin.com/in/slmkhanahmed/"
  }
};
